/**
 * Issue #757 — step 1 of 3 ("Where did you play?"), the pure half.
 *
 * Kept out of JSX for the reason every other `lib/*.js` in this folder
 * gives (`groundSuggestions.js`'s own header): this repo mounts no
 * components in unit tests, so a merge/labelling rule written only inside
 * JSX is checkable only by the e2e suite, which does not run per-commit.
 *
 * THE COPY LIVES HERE TOO, in one place, so the screen and its journeys
 * quote the same literal rather than two hand-typed copies drifting apart —
 * the same discipline `lib/nearMe.js`'s `NEAR_ME_LABEL` etc. already keep.
 * Every string below is verbatim from the issue.
 */


export const LOG_STEP_EYEBROW = "Log a match";
export const LOG_STEP_TITLE = "Where did you play?";
export const LOG_STEP_LEDE = "Three ways in. Take whichever is nearest to hand — then the rest is the scorecard.";

// D367.11a: the coach's Schedule a game step shows two ways, so it counts two.
export const LOG_STEP_LEDE_TWO = "Two ways in. Take whichever is nearest to hand.";

// D380 (#1448): the coach is scheduling a game that has not happened, so the
// same step speaks in the future tense. The player's log-a-match copy above
// is unchanged.
export const SCHEDULE_STEP_EYEBROW = "Schedule a game";
export const SCHEDULE_STEP_TITLE = "Where will you play?";

// D394 (Q34, extends D380): a player on Plan a match is planning a game that has
// not happened, so the same step speaks in the future tense too. Three ways in
// (the saved list stays), no scorecard line.
export const PLAN_STEP_EYEBROW = "Plan a match";
export const PLAN_STEP_LEDE = "Three ways in. Take whichever is nearest to hand.";
export const PLAN_STEP_CLOSING_LINE =
  "Not on any of the three? Name it in one line. No latitude, no longitude, and no second window.";

export const WAY_1_LABEL = "Use my location";
export const WAY_1_HELPER =
  "You are standing on it. Your phone knows where, and the grounds around you come back closest first.";
export const WAY_1_HELPER_SCHEDULE = "Your phone knows where, and the grounds around you come back closest first.";

export const WAY_2_LABEL = "Saved, or played at";
export const WAY_2_HELPER_PREFIX =
  "Your own grounds, yours first — the ones you have bookmarked and the ones you have played at.";

export const WAY_3_LABEL = "Search the world directory";
// Review round 2, B7 — `DirectionA1.dc.html:101` draws this placeholder;
// the build had "e.g. Thornmere Park" (GroundPicker's own placeholder, wrongly
// carried over).
export const LOG_STEP_SEARCH_PLACEHOLDER = "Type a ground or a town";
export const WAY_3_HELPER =
  "Search the directory or pick it off the map — the map is a fourth way in here, not a replacement for the " +
  "line above it: a ground saved without coordinates does not sit on a map, and coordinates are optional, so " +
  "the typed name stays.";

// D367.11a: the coach step has two numbered ways, so the map is the third.
export const WAY_3_HELPER_TWO = WAY_3_HELPER.replace("a fourth way in here", "a third way in here");

export const LOG_STEP_CLOSING_LINE =
  "Not on any of the three? Name it in one line and let your phone say where you are standing. No latitude, " +
  "no longitude, and no second window.";

export const LOG_STEP_CLOSING_LINE_TWO =
  "Not on either? Name it in one line. No latitude, no longitude, and no second window.";

// Review round 2, R1 (Andreas's decision, 2026-09-24) — the step's own way
// out, added by ruling rather than drawn: `DirectionA1`/`FirstGround` draw
// no exit at all. Kit `type.help` size, exactly "Back" — distinct from the
// add panel's own "Back to the list" (already below), which returns one
// level less far (to step 1, not out of the flow).
export const LOG_STEP_BACK_LABEL = "Back";

/**
 * Section 2's row label. `matches` comes straight off `GET /venues/played`'s
 * own `matches` field (`parity` with the shipped picker — see the issue's
 * §2.2) for a ground the player has actually played at; a bookmarked ground
 * with none gets the other line.
 *
 * Issue #768 (D96) — the date IS now real: `lastPlayed` is
 * `GET /venues/played`'s own `last_played` field (the latest valid
 * `match_date` for the group, or `null`), threaded straight through with no
 * second formatter (the raw string prints as-is, same convention
 * `recordFigures.js`'s `at {ground} on {match_date}` already uses). The
 * date only ever appears alongside a real match count: `n > 0` AND
 * `lastPlayed` a non-empty string prints "Played here N time(s) · last on
 * {date}"; any other combination — no matches, or matches with no valid
 * date — falls back to exactly today's two strings, never "last on —" and
 * never an empty date.
 */
export function playedAtLabel(matches, lastPlayed = null) {
  const n = Number(matches) || 0;
  if (n === 0) return "Saved · not played yet";
  const base = `Played here ${n} time${n === 1 ? "" : "s"}`;
  return typeof lastPlayed === "string" && lastPlayed.length > 0 ? `${base} · last on ${lastPlayed}` : base;
}

/**
 * Section 2 — "Saved, or played at". Merges the player's bookmarks
 * (`GET /players/me/grounds`) and their match history
 * (`GET /venues/played`) into ONE list, deduped by ground id (the same key
 * `GroundPicker.jsx`'s own `suggestions` memo uses), sorted A-Z — the
 * "existing merge" the issue's §2.2 points at, narrowed to the two
 * per-player sources (the shared directory is section 3's job, not this
 * one's).
 *
 * A row with no `ground_id` (a legacy free-text history entry that matches
 * no directory record) is dropped, same reason `GroundPicker.jsx` drops
 * one: it cannot be saved, so offering it would be offering a dead end.
 *
 * Issue #768 — `lastPlayed` rides along from `v.last_played` (`null` when
 * absent, and always `null` for a saved-only ground with no play history
 * at all — there is no date to have).
 *
 * #1610 — `latitude`/`longitude` ride along too (both endpoints return them),
 * so the map can plot these rows; `null` when the ground has none.
 */
export function mergeSavedAndPlayed(savedGrounds, venuesPlayed) {
  const byId = new Map();
  for (const v of venuesPlayed || []) {
    if (!v.ground_id) continue;
    byId.set(v.ground_id, {
      name: v.ground,
      groundId: v.ground_id,
      city: v.city ?? null,
      country: v.country ?? null,
      matches: Number(v.matches) || 0,
      lastPlayed: v.last_played ?? null,
      latitude: v.latitude ?? null,
      longitude: v.longitude ?? null,
    });
  }
  for (const g of savedGrounds || []) {
    if (!g.ground_id) continue;
    const existing = byId.get(g.ground_id);
    byId.set(g.ground_id, {
      name: g.name,
      groundId: g.ground_id,
      city: g.city ?? existing?.city ?? null,
      country: g.country ?? existing?.country ?? null,
      matches: existing?.matches ?? 0,
      lastPlayed: existing?.lastPlayed ?? null,
      latitude: g.latitude ?? existing?.latitude ?? null,
      longitude: g.longitude ?? existing?.longitude ?? null,
    });
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/* -------------------------------------------------------------------------
 * The add-a-ground panel (issue #757 §3) — the same step, not a second
 * window. Every string below is verbatim from the issue.
 * ---------------------------------------------------------------------- */

// Round 5 conformance — `ADD_GROUND_STEP_LABEL` ("add a ground") is gone.
// Round 2 guessed the top bar read "Step 1 of 3 · add a ground" on this
// panel; `FirstGround.dc.html:44`'s own rendered span (not its header
// comment, which still says otherwise) reads plain "Step 1 of 3", same as
// `DirectionA1.dc.html`. `LogGroundFlow.jsx` no longer passes a `label` to
// `StepCounter` at all.
// Review round 2, B7 — `FirstGround.dc.html:48`'s own eyebrow (missing from
// round 1's build).
export const ADD_GROUND_EYEBROW = "Somewhere else";
// #1220 — the "Where I'm standing" menu choice's own eyebrow: the board's word
// for that choice, so the panel does not contradict what was just picked.
export const ADD_GROUND_EYEBROW_HERE = "Where I'm standing";
// #1220 — a dropped pin (D285): the pin-path wording, so the panel never says
// the phone's location is used. PROPOSED, awaiting a D-code.
export const ADD_GROUND_PIN_LEDE = "It isn't in the list yet. Name it, and we'll keep the pin you dropped.";
export const ADD_GROUND_PIN_LABEL = "The pin you dropped";
export const ADD_GROUND_PIN_ACTION_LABEL = "Add it at this pin";
export const ADD_GROUND_TITLE = "Name the ground you're at";
export const ADD_GROUND_LEDE =
  "It isn't in the list yet. Name it, and let your phone say where you're standing. That's the whole of it.";
// D394 follow-up (#1473): Plan a match and Schedule a game add a ground for a game that has
// not happened, so the lede carries no standing-on-it idea. ADD_GROUND_LEDE is unchanged.
export const ADD_GROUND_LEDE_FUTURE = "It isn't in the list yet. Name it. That's the whole of it.";

export const ADD_GROUND_NAME_PLACEHOLDER = "What do people call it?";

// D155 (#884, Andreas, 2026-09-25, decisions-2026-09-24.md) replaces
// `FirstGround.dc.html:77`'s drawn "Pick one — it's how people tell a
// ground from a practice facility." — already under "Place type" in this
// build, so D155's own "moves up under 'Place type'" describes the
// round-6 canvas's change, not a further relocation here. Indoor as an
// attribute of a field/net needs a schema change and is a wave-5 issue.
export const ADD_GROUND_PLACE_TYPE_HELPER =
  "Nets only here? Pick Nets. Nets at a ground get added to that ground.";

export const ADD_GROUND_LOCATION_LABEL = "Where you're standing";
export const ADD_GROUND_LOCATION_HELPER =
  "One tap. Your phone knows the spot and we keep it as the ground's. No latitude, no longitude, nothing to " +
  "look up — and this is still the same screen you were filling in.";
export const ADD_GROUND_LOCATION_SAVED_NOTE = "Saved to your grounds, so next time it's already in the list.";

export const ADD_GROUND_ACTION_LABEL = "Add it from where I am";

export const ADD_GROUND_DECLINE_LABEL = "If you'd rather not share it";
// Review round 2, B7 — `FirstGround.dc.html:88`'s own field label (missing
// from round 1's build).
export const ADD_GROUND_TOWN_FIELD_LABEL = "City / Town";
export const ADD_GROUND_TOWN_PLACEHOLDER = "Type the town instead";
export const ADD_GROUND_SAVE_TOWN_LABEL = "Save with this town";

export const ADD_GROUND_GEOCODE_FAILED =
  "We couldn't work out where that is. Try the location tap instead, or check the spelling.";

export const ADD_GROUND_CLOSING_LINE =
  "The pin can come later, from the ground card — never from you, standing at the rope.";

// Issue #1080 (D212.4) — the duplicate check the add panel asks before it
// posts. Undrawn by ruling: `FirstGround.dc.html` draws no duplicate state.
// Copy is verbatim from the issue; the failed line follows the shipped
// dialog's own rule ("you can still save", `GroundSearchDialog` #633).
export const ADD_GROUND_DUPLICATE_EYEBROW = "Already on the map?";
export const ADD_GROUND_DUPLICATE_LINE =
  "A ground with this name is already here. Use it instead of adding another:";
export const ADD_GROUND_USE_EXISTING_LABEL = "Use this ground";
/** D284 — the Grounds host's third option, styled like the D257 warning's other action. */
export const ADD_GROUND_ADD_SURFACE_LABEL = "Add a field or net to it instead";
export const ADD_GROUND_ADD_ANYWAY_LABEL = "Add it anyway";
export const ADD_GROUND_DUPLICATE_CHECK_FAILED =
  "We couldn't check whether this ground is already here. You can still save it.";
export const ADD_GROUND_DUPLICATE_CHECK_RETRY = "Check again";

// D257 — rows the check offered on position alone are not "the same name":
// say so. Heading for a result where no row's name relates to what was typed.
export const ADD_GROUND_DUPLICATE_NEARBY_LINE = "Is it one of these grounds nearby?";

// F3 (#1080 review) — the "Use this ground" failure line, with the other copy.
export const ADD_GROUND_USE_FAILED = "Could not save this ground. Try again, or add it anyway.";

/**
 * #1610 — which rows the Log a match map plots: the first source that has a
 * row with coordinates. A typed search wins, then the near-me ranking, then
 * the player's saved or played grounds (`mergeSavedAndPlayed`). `source`
 * names the winner so the caller can pick the framing; "none" leaves the
 * band on its fallback view.
 */
export function chooseMapRows({ typed, searchRows, nearMe, savedOrPlayed }) {
  // The same test the band plots with (`LogGroundMapBand`), so a source never wins with rows it cannot draw.
  const plottable = (rows) => (rows || []).filter((g) => Number.isFinite(g.latitude ?? g.lat) && Number.isFinite(g.longitude ?? g.lng));
  if (typed) {
    const rows = plottable(searchRows);
    if (rows.length > 0) return { source: "search", rows };
  }
  if (nearMe?.status === "ready") {
    const rows = plottable(nearMe.results);
    if (rows.length > 0) return { source: "near", rows };
  }
  const rows = plottable(savedOrPlayed);
  if (rows.length > 0) return { source: "saved", rows };
  return { source: "none", rows: [] };
}
